export type GoogleAnalyticsConsent = "granted" | "denied";

export const GOOGLE_ANALYTICS_CONSENT_STORAGE_KEY =
  "awkwardpixels:google-analytics-consent:v1";

const GOOGLE_ANALYTICS_ID_PATTERN = /^G-[A-Z0-9]{4,20}$/;
const GOOGLE_ANALYTICS_SCRIPT_ATTRIBUTE = "data-awkwardpixels-google-analytics";

const deniedConsent = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
} as const;

const analyticsGrantedConsent = {
  ...deniedConsent,
  analytics_storage: "granted",
} as const;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export function normalizeGoogleAnalyticsMeasurementId(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toUpperCase();
  return GOOGLE_ANALYTICS_ID_PATTERN.test(normalized) ? normalized : null;
}

export function readGoogleAnalyticsConsent(): GoogleAnalyticsConsent | null {
  try {
    const stored = window.localStorage.getItem(GOOGLE_ANALYTICS_CONSENT_STORAGE_KEY);
    return stored === "granted" || stored === "denied" ? stored : null;
  } catch {
    return null;
  }
}

export function saveGoogleAnalyticsConsent(consent: GoogleAnalyticsConsent): void {
  try {
    window.localStorage.setItem(GOOGLE_ANALYTICS_CONSENT_STORAGE_KEY, consent);
  } catch {
    // A blocked storage API means the visitor will be asked again next time.
  }
}

export function startGoogleAnalytics(measurementId: unknown): boolean {
  const normalizedId = normalizeGoogleAnalyticsMeasurementId(measurementId);
  if (!normalizedId || typeof window === "undefined" || typeof document === "undefined") {
    return false;
  }

  if (document.querySelector(`script[${GOOGLE_ANALYTICS_SCRIPT_ATTRIBUTE}]`)) {
    return true;
  }

  window.dataLayer ??= [];
  window.gtag ??= function googleAnalyticsQueue() {
    // gtag.js processes the Arguments object used by Google's standard snippet.
    // Pushing a rest-parameter Array leaves consent and config commands ignored.
    window.dataLayer?.push(arguments);
  };

  // Basic Consent Mode: nothing from Google is loaded before the visitor opts in.
  window.gtag("consent", "default", deniedConsent);
  window.gtag("consent", "update", analyticsGrantedConsent);
  window.gtag("set", "allow_google_signals", false);
  window.gtag("set", "allow_ad_personalization_signals", false);
  window.gtag("js", new Date());
  window.gtag("config", normalizedId, {
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    send_page_view: true,
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(normalizedId)}`;
  script.setAttribute(GOOGLE_ANALYTICS_SCRIPT_ATTRIBUTE, normalizedId);
  document.head.append(script);
  return true;
}

export function revokeGoogleAnalytics(): void {
  window.gtag?.("consent", "update", deniedConsent);
  clearGoogleAnalyticsCookies();
  document
    .querySelectorAll(`script[${GOOGLE_ANALYTICS_SCRIPT_ATTRIBUTE}]`)
    .forEach((script) => script.remove());
}

function clearGoogleAnalyticsCookies(): void {
  const analyticsCookieNames = document.cookie
    .split(";")
    .map((cookie) => cookie.split("=", 1)[0]?.trim())
    .filter((name): name is string =>
      Boolean(name && (name === "_ga" || name.startsWith("_ga_") || name === "_gid")),
    );

  const hostname = window.location.hostname;
  const domainSuffixes = hostname && hostname !== "localhost"
    ? [`; domain=${hostname}`, `; domain=.${hostname}`]
    : [];

  for (const name of analyticsCookieNames) {
    const removal = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
    document.cookie = removal;
    for (const suffix of domainSuffixes) document.cookie = `${removal}${suffix}`;
  }
}
